/**
 * SUPERVISOR-CONSULTANTS-LIST
 * Consulting accounts : number of clients and last client created (search and sort)
 */

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faPencilAlt, faPlayCircle, faShieldAlt } from "@fortawesome/free-solid-svg-icons"
import { orderBy } from "lodash"
import Card from "@/components/card"
import Chip from "@/components/chip"
import ListButton from "@/components/list-button"
import ListCell from "@/components/list-cell"
import ListContainer from "@/components/list-container"
import ListFields from "@/components/list-fields"
import ListIcon from "@/components/list-icon"
import ListItem from "@/components/list-item"
import PageLoader from "@/components/page-loader"
import Space from "@/components/space"
import { store } from "@/index"
import { STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import { accountFetchParents_AsSupervisor } from "@/redux/account.actions"
import { Account } from "@/redux/account.types"
import { formatDate } from "@/utils/format-date.utils"
import { getImageUrl } from "@/utils/get-image-url"

interface Props {
  onClick: (account: Account) => void //Connect to the account
  onEdit: (account: Account) => void //Edit the account
  salesId: string | null //Only the consultants of this AE
}

//Consultant : account with its number of clients and its last client
interface Consultant {
  account: Account
  clientsCount: number
  lastClientName: string | null
  lastClientDate: string | null
}

//Columns to sort
type SortField = "name" | "clientsCount" | "lastClientDate"

function SupervisorConsultantsListWidget(props: Props) {
  const { t } = useTranslation()

  const [consultants, setConsultants] = useState<Consultant[]>([])
  const [status, setStatus] = useState<string>(STATUS_LOADING)
  const [search, setSearch] = useState<string>("")
  const [sort, setSort] = useState<{ field: SortField; direction: "asc" | "desc" }>({
    field: "lastClientDate",
    direction: "desc"
  })

  useEffect(() => {
    async function load() {
      const response: any = await store.dispatch(accountFetchParents_AsSupervisor())
      setConsultants(
        response.error
          ? []
          : response.map((x: any) => ({
              account: new Account(x),
              clientsCount: x.clientsCount || 0,
              lastClientName: x.lastClientName,
              lastClientDate: x.lastClientDate
            }))
      )
      setStatus(STATUS_LOADED)
    }

    load()
  }, [])

  //Clic on a column : sort on it (twice : reverse the order)
  function sortBy(field: SortField) {
    setSort({ field, direction: sort.field === field && sort.direction === "desc" ? "asc" : "desc" })
  }

  //Consultants searched then sorted
  const displayed: Consultant[] = orderBy(
    consultants.filter(
      (x) =>
        (!props.salesId || x.account.SupervisorIdSales === props.salesId) &&
        x.account.name.toLowerCase().includes(search.trim().toLowerCase())
    ),
    [(x) => (sort.field === "name" ? x.account.name.toLowerCase() : (x[sort.field] ?? ""))],
    [sort.direction]
  )

  return (
    <Card isWithoutPadding isOverflowHidden>
      <ListContainer onSearch={setSearch}>
        <ListFields>
          <ListCell />
          <ListCell width={250} text={t("account")} onSort={() => sortBy("name")} />
          <ListCell width={150} text="Clients" onSort={() => sortBy("clientsCount")} />
          <ListCell width={300} text="Dernier client" onSort={() => sortBy("lastClientDate")} />
        </ListFields>

        <PageLoader status={status}>
          {displayed.map((x) => (
            <ListItem key={x.account.id} isEditable onClick={() => props.onClick(x.account)}>
              <ListIcon
                fallbackIcon={faShieldAlt}
                iconColor={x.account.colors.brandPrimary}
                image={x.account.options.image ? getImageUrl("Accounts", x.account.id) : null}
              />

              <ListCell width={250}>{x.account.name}</ListCell>

              <ListCell width={150}>
                <Chip count={x.clientsCount} />
              </ListCell>

              <ListCell width={300}>
                {x.lastClientName && (
                  <div>
                    <div>{x.lastClientName}</div>
                    <div className="grey-t text-sm">{formatDate(x.lastClientDate, false, false)}</div>
                  </div>
                )}
              </ListCell>

              <Space />

              <ListButton icon={faPencilAlt} onClick={() => props.onEdit(x.account)} text={t("account_edit")} />
              <ListButton
                icon={faPlayCircle}
                onClick={() => props.onClick(x.account)}
                isPrimary
                text={t("utils_connect")}
              />
            </ListItem>
          ))}
        </PageLoader>
      </ListContainer>
    </Card>
  )
}

export default SupervisorConsultantsListWidget
